<!--
  TinoLearningCurve.vue — Area chart showing leads vs closed sales over 8 months.
  Uses vue-echarts with the app's 'sensando' theme.
-->
<script setup>
import { computed, provide } from 'vue'
import { useI18n } from 'vue-i18n'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import VChart, { THEME_KEY } from 'vue-echarts'

use([CanvasRenderer, LineChart, GridComponent, TooltipComponent, LegendComponent])
provide(THEME_KEY, 'sensando')

const { t } = useI18n()

const months = ['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr']
const leadsData  = [120, 145, 132, 158, 170, 163, 195, 210]
const salesData  = [ 34,  52,  48,  70,  82,  78, 104, 118]

const option = computed(() => ({
  grid: { top: 8, right: 16, bottom: 24, left: 40, containLabel: false },
  xAxis: {
    type: 'category',
    data: months,
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { fontSize: 11, color: 'rgba(0,0,0,0.45)' },
  },
  yAxis: {
    type: 'value',
    splitLine: { lineStyle: { color: 'rgba(0,0,0,0.06)' } },
    axisLabel: { fontSize: 10, color: 'rgba(0,0,0,0.45)' },
  },
  tooltip: { trigger: 'axis' },
  legend: { show: false },
  series: [
    {
      name: t('dashboard.tino.learning.leads'),
      type: 'line',
      data: leadsData,
      smooth: true,
      symbol: 'none',
      lineStyle: { color: '#6366f1', width: 2.5 },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(99,102,241,0.3)' },
            { offset: 1, color: 'rgba(99,102,241,0.0)' },
          ],
        },
      },
    },
    {
      name: t('dashboard.tino.learning.sales'),
      type: 'line',
      data: salesData,
      smooth: true,
      symbol: 'none',
      lineStyle: { color: '#38bdf8', width: 2.5 },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(56,189,248,0.25)' },
            { offset: 1, color: 'rgba(56,189,248,0.0)' },
          ],
        },
      },
    },
  ],
}))
</script>

<template>
  <VCard class="tino-curve-card" flat>
    <!-- Header -->
    <div class="tino-curve-header">
      <div class="d-flex align-center gap-2">
        <VIcon icon="mdi-trending-up" size="18" color="primary" />
        <span class="text-sm font-weight-semibold">{{ t('dashboard.tino.learning.title') }}</span>
      </div>
      <!-- Legend -->
      <div class="d-flex align-center gap-3">
        <div class="d-flex align-center gap-1">
          <span class="tino-dot" style="background:#6366f1" />
          <span class="text-xs text-medium-emphasis">{{ t('dashboard.tino.learning.leads') }}</span>
        </div>
        <div class="d-flex align-center gap-1">
          <span class="tino-dot" style="background:#38bdf8" />
          <span class="text-xs text-medium-emphasis">{{ t('dashboard.tino.learning.sales') }}</span>
        </div>
      </div>
    </div>

    <VDivider />

    <!-- Chart -->
    <div class="tino-curve-chart">
      <VChart :option="option" autoresize style="height: 200px; width: 100%;" />
    </div>
  </VCard>
</template>

<style scoped lang="scss">
.tino-curve-card {
  border: 1px solid rgba(var(--v-border-color), 0.1);
  border-radius: 16px !important;
  background: rgb(var(--v-theme-surface));
  overflow: hidden;
  block-size: 100%;
}

.tino-curve-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
}

.tino-curve-chart {
  padding: 8px 8px 12px;
}

.tino-dot {
  display: inline-block;
  inline-size: 8px;
  block-size: 8px;
  border-radius: 50%;
}
</style>
